import { ReactNode, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    PiFunnelBold,
    PiMagnifyingGlassBold,
} from 'react-icons/pi';
import { Badge, Input, LoadingState, PageHeader, Text } from '@v2/design-system';
import TablePagination, { TABLE_PER_PAGE_OPTIONS } from '@v2/components/TablePagination';
import { cn } from '@v2/utils/cn';

export type BadgeTone = 'primary' | 'info' | 'success' | 'warning' | 'danger' | 'secondary';

export function toneFromFilament(color: string | null | undefined): BadgeTone {
    switch (color) {
        case 'primary': return 'primary';
        case 'info': return 'info';
        case 'success': return 'success';
        case 'warning': return 'warning';
        case 'danger': return 'danger';
        default: return 'secondary';
    }
}

export type Column<T> = {
    key: string;
    label: string;
    align?: 'left' | 'right' | 'center';
    width?: string;
    render?: (row: T) => ReactNode;
};

export type PaginationMeta = {
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
    from: number | null;
    to: number | null;
};

type RichListShellProps<T> = {
    title: string;
    breadcrumb?: { name: string; href?: string }[];
    headerActions?: ReactNode;
    /** Bloco entre o título e a tabela (indicadores, avisos). */
    intro?: ReactNode;
    search: string;
    onSearch: (v: string) => void;
    searchPlaceholder?: string;
    filtersPanel?: ReactNode;
    activeFiltersCount?: number;
    isLoading: boolean;
    columns: Column<T>[];
    rows: T[];
    rowKey: (r: T) => string | number;
    detailPath?: (r: T) => string;
    detailLabel?: string;
    actionsRender?: (r: T) => ReactNode;
    totalLabel?: string;
    emptyMessage?: string;
    pagination?: PaginationMeta;
    onPageChange?: (page: number) => void;
    onPerPageChange?: (perPage: number) => void;
};

export default function RichListShell<T>({
    title,
    breadcrumb,
    headerActions,
    intro,
    search,
    onSearch,
    searchPlaceholder = 'Pesquisar',
    filtersPanel,
    activeFiltersCount,
    isLoading,
    columns,
    rows,
    rowKey,
    detailPath,
    actionsRender,
    totalLabel,
    emptyMessage = 'Nenhum registro encontrado.',
    pagination,
    onPageChange,
    onPerPageChange,
}: RichListShellProps<T>) {
    const [filtersOpen, setFiltersOpen] = useState(false);
    const navigate = useNavigate();
    const showFiltersBtn = Boolean(filtersPanel);

    return (
        <div className="space-y-6">
            <PageHeader title={title} breadcrumb={breadcrumb}>
                {headerActions}
            </PageHeader>

            {intro}

            <div className="ds-table-container rounded-2xl shadow-sm">
                {/* Barra de busca e filtros */}
                <div className="flex flex-wrap items-center justify-end gap-2 border-b border-gray-100 px-4 py-3 dark:border-gray-700/50">
                    <div className="ms-auto flex items-center gap-2">
                        <Input
                            type="search"
                            value={search}
                            onChange={(e) => onSearch(e.target.value)}
                            clearable
                            onClear={() => onSearch('')}
                            prefix={<PiMagnifyingGlassBold className="size-4" />}
                            placeholder={searchPlaceholder}
                            inputClassName="h-9"
                            className="w-72"
                        />
                        {showFiltersBtn && (
                            <button
                                type="button"
                                onClick={() => setFiltersOpen((v) => !v)}
                                className="relative rounded-md border border-gray-200 p-2 text-gray-600 transition-colors hover:bg-gray-50 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-gray-800"
                                title="Filtros"
                            >
                                <PiFunnelBold className="size-4" />
                                {(activeFiltersCount ?? 0) > 0 && (
                                    <span className="absolute -end-1 -top-1 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-orange-100 px-1 text-[10px] font-semibold text-orange-700 dark:bg-orange-900/50 dark:text-orange-400">
                                        {activeFiltersCount}
                                    </span>
                                )}
                            </button>
                        )}
                    </div>
                </div>

                {showFiltersBtn && filtersOpen && (
                    <div className="border-b border-gray-100 px-4 py-3 dark:border-gray-700/50">
                        {filtersPanel}
                    </div>
                )}

                {isLoading ? (
                    <LoadingState variant="table" count={8} />
                ) : (
                    <div className="overflow-x-auto">
                        <table className="min-w-full text-sm">
                            <thead className="border-b border-gray-100 text-xs uppercase tracking-wide text-gray-500">
                                <tr>
                                    {columns.map((c) => (
                                        <th
                                            key={c.key}
                                            className={`px-4 py-3 font-semibold ${alignClass(c.align)}`}
                                            style={c.width ? { width: c.width } : undefined}
                                        >
                                            {c.label}
                                        </th>
                                    ))}
                                    {actionsRender && (
                                        <th className="px-4 py-3 text-right font-semibold"></th>
                                    )}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 dark:divide-zinc-800/60">
                                {rows.map((row) => (
                                    <tr
                                        key={rowKey(row)}
                                        onClick={detailPath ? () => navigate(detailPath(row)) : undefined}
                                        className={`transition hover:bg-gray-50/60 dark:hover:bg-zinc-800/40 ${detailPath ? 'cursor-pointer' : ''}`}
                                    >
                                        {columns.map((c) => (
                                            <td key={c.key} className={`px-4 py-3 text-gray-700 dark:text-zinc-300 ${alignClass(c.align)}`}>
                                                {c.render
                                                    ? c.render(row)
                                                    : (row as Record<string, unknown>)[c.key] as ReactNode ?? '—'}
                                            </td>
                                        ))}
                                        {actionsRender && (
                                            <td
                                                className="px-4 py-3 text-right"
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                <div className="inline-flex items-center gap-2">
                                                    {actionsRender(row)}
                                                </div>
                                            </td>
                                        )}
                                    </tr>
                                ))}
                                {rows.length === 0 && (
                                    <tr>
                                        <td
                                            colSpan={columns.length + (actionsRender ? 1 : 0)}
                                            className="px-4 py-12 text-center text-sm text-gray-500"
                                        >
                                            {emptyMessage}
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* Rodapé: total + per-page + paginação */}
                {pagination && onPageChange ? (
                    <TablePagination
                        meta={pagination}
                        onPageChange={onPageChange}
                        onPerPageChange={onPerPageChange}
                        perPageOptions={TABLE_PER_PAGE_OPTIONS}
                        itemLabel="registro"
                    />
                ) : totalLabel ? (
                    <div className="border-t border-gray-100 px-4 py-3 dark:border-gray-700/50">
                        <Text className="text-xs text-gray-500 dark:text-zinc-500">{totalLabel}</Text>
                    </div>
                ) : null}
            </div>
        </div>
    );
}

function alignClass(a?: 'left' | 'right' | 'center') {
    if (a === 'right') return 'text-right';
    if (a === 'center') return 'text-center';
    return 'text-left';
}

export function BadgePill({ tone, children }: { tone: BadgeTone; children: ReactNode }) {
    return (
        <Badge color={tone} variant="flat" rounded="pill" size="sm">
            {children}
        </Badge>
    );
}
